import {toDecimalFr, useAffichagePrixTtc} from 'ff-common';
import {
  Box,
  HStack,
  IconButton,
  Input,
  StyleProps,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {CheckIcon, CloseIcon, EditIcon} from '@chakra-ui/icons';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {PrixTag} from '@/domains/catalogue/components/PrixTag';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {usePromoLignePanier} from '@/hooks/usePromoLignePanier';
import {EditionPrixState, usePriceEditor} from '@/hooks/panier/usePriceEditor';
import {ModeTarifaireToggle} from '@/domains/panier/PrixEditor/ModeTarifaireToggle';
import {ResetPriceButton} from '@/domains/panier/PrixEditor/ResetPriceButton';
import {PrixPostPromotionTooltip} from '@/domains/panier/PrixEditor/PrixPostPromotionTooltip';
import {PRICE_INPUT_PATTERN, PRICE_INPUT_TITLE} from '@/utils/price-validation';
import {useClient} from '@/haligator-factory';

export const PrixBlock: React.FC<
  {
    refreshing: boolean;
    ligneDePanier: State<LigneDePanierDesc>;
    /** Remonte le draft au parent pour le preview des marges. */
    onEditionPrixChange?: (state: EditionPrixState) => void;
  } & StyleProps
> = ({refreshing, ligneDePanier, onEditionPrixChange, ...props}) => {
  const {t} = useTranslation();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());

  // Spécificités propre au commercial : tag de catégorie de prix
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    undefined,
    {contexte: 'panier'},
  );
  // Promotion du catalogue, visible par tous contrairement au tag de catégorie ci-dessus,
  // soumis à permission. Barrée quand elle ne couvre pas la date de livraison du panier.
  const promo = usePromoLignePanier(ligneDePanier);
  const hasTag = !!(categorieDeTarif ?? modeTarifaireSouhaite) || !!promo;

  const {
    isEditing,
    prixText,
    setPrixText,
    isDerogation,
    utilisateurDeroAutorisee,
    modifierPrixCommand,
    isPrixModifie,
    peutEditer,
    handleStartEdit,
    handleSave,
    handleCancel,
    handleSupprimerPrix,
    handleKeyDown,
    handleToggleDerogation,
  } = usePriceEditor({ligneDePanier, onEditionPrixChange});

  // TTC pour le client et la Vue Client ; le commercial reste en HT (règle portée par useAffichagePrixTtc)
  // Sans prix unitaire TTC sur la ligne, tout le bloc reste en HT, total compris
  const prixUnitaireTtc = ligneDePanier.data.prix_unitaire_ttc;
  const afficherTtc = affichagePrixTtc && prixUnitaireTtc !== undefined;
  const prixPrincipal = afficherTtc
    ? ligneDePanier.data.total_estime_ttc
    : ligneDePanier.data.prix_total_estime_ht;
  const prixUnitaire = ligneDePanier.data.prix_unitaire;
  const prixUnitaireAffiche = afficherTtc ? prixUnitaireTtc : prixUnitaire;
  const uniteFacturation = ligneDePanier.data.unite_de_facturation;
  const isSubmitting = modifierPrixCommand.isSubmitting;
  const labelTaxe = afficherTtc
    ? t('ligne_produit.TTC')
    : t('ligne_produit.HT');

  return (
    <VStack
      minW={{base: 0, md: '9em'}}
      alignItems='start'
      gap='0'
      mt={{base: '15px', md: hasTag ? '-12px' : '15px'}}
      ml='auto'
      mr={{base: 0, md: 7}}
      {...props}
    >
      {hasTag && (
        <PrixTag
          categorieDeTarif={categorieDeTarif}
          modeTarifaireSouhaite={modeTarifaireSouhaite}
          promo={promo}
          styleProps={{mb: 1}}
        />
      )}
      <HStack
        gap='0.5'
        alignItems='start'
      >
        <Text
          textColor='black.text'
          fontSize='xl'
          fontWeight='700'
          opacity={refreshing ? 0.3 : 1}
        >
          {toDecimalFr(prixPrincipal)}
        </Text>
        <Text fontSize='xs'>€</Text>
        <Text fontSize='xs'>{labelTaxe}</Text>
      </HStack>
      {isEditing ? (
        <VStack
          alignItems='start'
          gap='1'
          mt='1'
        >
          <HStack
            spacing={1}
            bg='blue.main_transparency_10'
            borderRadius='8px'
            p={1}
          >
            <Input
              value={prixText}
              onChange={(e) => {
                setPrixText(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              type='text'
              pattern={PRICE_INPUT_PATTERN}
              title={PRICE_INPUT_TITLE}
              size='sm'
              width='80px'
              bg='white'
              borderRadius='6px'
              fontSize='sm'
              fontWeight='600'
              autoFocus
              isDisabled={isSubmitting}
            />
            <IconButton
              icon={<CheckIcon />}
              aria-label={t('global.valider')}
              size='xs'
              colorScheme='green'
              variant='ghost'
              onClick={handleSave}
              isLoading={isSubmitting}
            />
            <IconButton
              icon={<CloseIcon boxSize={2.5} />}
              aria-label={t('global.annuler')}
              size='xs'
              variant='ghost'
              color='gray.dark'
              onClick={handleCancel}
              isDisabled={isSubmitting}
            />
          </HStack>
          <ModeTarifaireToggle
            isDerogation={isDerogation}
            onChange={handleToggleDerogation}
            isHidden={!utilisateurDeroAutorisee}
            isDisabled={isSubmitting}
          />
        </VStack>
      ) : peutEditer ? (
        <PrixPostPromotionTooltip
          ligneDePanier={ligneDePanier}
          shouldShow={true}
        >
          <Box
            role='group'
            cursor='pointer'
            onClick={() => {
              handleStartEdit(prixUnitaire);
            }}
            borderRadius='8px'
            px={1}
            mx={-1}
            opacity={isSubmitting ? 0.5 : 1}
            _hover={{bg: 'blue.main_transparency_10'}}
            transition='background 0.15s'
          >
            <HStack
              gap={1}
              alignItems='center'
            >
              <Text
                fontSize='xs'
                color='gray.dark'
              >
                {t('ligne_produit.soit_prix', {
                  prix: toDecimalFr(prixUnitaire, 2, 3),
                })}
                /{t(`unite.${uniteFacturation}`, {count: 1})}
              </Text>
              <HStack
                opacity={0}
                _groupHover={{opacity: 1}}
                transition='opacity 0.2s'
                spacing={0}
                alignItems='center'
              >
                <Tooltip
                  label={t('global.modifier')}
                  placement='top'
                >
                  <Box
                    color='blue.main'
                    display='flex'
                    alignItems='center'
                  >
                    <EditIcon boxSize={3} />
                  </Box>
                </Tooltip>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  display='flex'
                  alignItems='center'
                  visibility={isPrixModifie ? 'visible' : 'hidden'}
                >
                  <ResetPriceButton
                    onReset={handleSupprimerPrix}
                    isLoading={isSubmitting}
                  />
                </Box>
              </HStack>
            </HStack>
          </Box>
        </PrixPostPromotionTooltip>
      ) : (
        <PrixPostPromotionTooltip
          ligneDePanier={ligneDePanier}
          shouldShow={true}
        >
          <Text
            fontSize='xs'
            color='gray.dark'
          >
            {t('ligne_produit.soit_prix', {
              prix: toDecimalFr(prixUnitaireAffiche, 2, 3),
            })}
            /{t(`unite.${uniteFacturation}`, {count: 1})}
          </Text>
        </PrixPostPromotionTooltip>
      )}
    </VStack>
  );
};
